import React, { useMemo } from 'react'
import * as THREE from 'three'

interface BarProps {
  index: number
  width?: number
  depth?: number
  height: number
  spacing?: number
  color?: string
}

const Bar: React.FC<BarProps> = ({
  index,
  width = 0.5,
  depth = 0.5,
  height,
  spacing = 1.2,
  color = '#00ffff'
}) => {
  const geometry = useMemo(() => new THREE.BoxGeometry(width, 1, depth), [width, depth])
  
  const edgesGeometry = useMemo(() => {
    const edges = new THREE.EdgesGeometry(geometry)
    const posAttr = edges.getAttribute('position')
    const positions = posAttr.array as number[]
    const lineDistances: number[] = []
    for (let i = 0; i < positions.length; i += 6) {
      const x1 = positions[i]
      const y1 = positions[i + 1]
      const z1 = positions[i + 2]
      const x2 = positions[i + 3]
      const y2 = positions[i + 4]
      const z2 = positions[i + 5]
      const segmentLength = Math.sqrt(
        (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2
      )
      lineDistances.push(0, segmentLength)
    }
    edges.setAttribute(
      'lineDistance',
      new THREE.Float32BufferAttribute(lineDistances, 1)
    )
    return edges
  }, [geometry])

  const baseDashSize = 0.1
  const baseGapSize = 0.05
  const adjustedDashSize = baseDashSize / height
  const adjustedGapSize = baseGapSize / height

  return (
    <group position={[index * spacing, height / 2, 0]} scale={[1, height, 1]}>
      <mesh geometry={geometry}>
        <meshBasicMaterial
          color={color}
          polygonOffset
          polygonOffsetFactor={1}
          polygonOffsetUnits={1}
        />
      </mesh>
      <lineSegments geometry={edgesGeometry}>
        <lineDashedMaterial
          color="#ffffff"
          dashSize={adjustedDashSize}
          gapSize={adjustedGapSize}
          linewidth={1}
          depthTest={true}
          depthFunc={THREE.GreaterDepth}
        />
      </lineSegments>
      <lineSegments geometry={edgesGeometry}>
        <lineBasicMaterial color="#ffffff" linewidth={1} />
      </lineSegments>
    </group>
  )
}

const VisualizationInsights: React.FC = () => {
  const barCount = 5
  const spacing = 1.2
  const heights = [2.5, 1.8, 3.2, 2.0, 4.0, 3.5, 1.2, 2.8, 3.6, 2.2]
  
  const colors = [
    '#d4f0fc',
    '#89d6fb',
    '#02a9f7',
    '#02577a',
    '#01303f',
  ]

  const totalWidth = (barCount - 1) * spacing
  const offsetX = -totalWidth

  return (
    <group rotation={[0, Math.PI / 4, 0]} position={[offsetX, -4, 0]}>
      {Array.from({ length: barCount }, (_, i) => (
        <Bar
          key={i}
          index={i}
          spacing={spacing}
          height={heights[i % heights.length]}
          color={colors[i % colors.length]}
        />
      ))}
    </group>
  )
}

export default VisualizationInsights
